import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import { Button, Input } from '../../components/common/UI';
import logoImg from '../../assets/logo.png';

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await authAPI.forgotPassword(data);
      setSent(true);
      toast.success('Password reset link sent to your email');
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.msg || e.message || 'Failed to send reset link';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex gradient-mesh">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-16 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-95" />
        <div className="relative z-10 text-white text-center max-w-sm">
          <div className="mb-6 flex items-center justify-center">
            <img src={logoImg} alt="Bellezbuy Admin" className="h-20 w-20 object-contain drop-shadow-xl" />
          </div>
          <h1 className="font-display text-4xl font-bold mb-4">Forgot Password?</h1>
          <p className="text-white/80 text-lg leading-relaxed">No worries! Just enter your registered email address, and we'll send you instructions to reset your password.</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex w-full lg:w-1/2 flex-col justify-center items-center p-8">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={logoImg} alt="Bellezbuy Admin" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-surface-900 text-lg">Bellezbuy Admin</span>
          </div>

          {!sent ? (
            <>
              <h2 className="font-display text-2xl font-bold text-surface-900 mb-1">Reset your password</h2>
              <p className="text-sm text-surface-500 mb-8">We will send a reset link to your email address</p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Input
                  label="Email address"
                  type="email"
                  placeholder="you@company.com"
                  error={errors.email?.message}
                  {...register('email', { required: 'Email is required', pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' } })}
                />
                <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
                  Send Reset Link
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-50 text-success-600 mx-auto">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l8-5.333a2 2 0 012.22 0l8 5.333A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                </svg>
              </div>
              <h2 className="font-display text-2xl font-bold text-surface-900 mb-2">Check your email</h2>
              <p className="text-sm text-surface-500 mb-8">
                We have sent password recovery instructions to your email address.
              </p>
              <Button onClick={() => setSent(false)} variant="secondary" size="md" className="w-full">
                Resend link
              </Button>
            </div>
          )}

          <p className="mt-6 text-center text-xs text-surface-500">
            Remembered your password?{' '}
            <Link to="/login" className="text-brand-600 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
